import type { ChatListEntry, ChatMessageHit } from "@altek/ui/components/chat/ChatList";
import type { ChatFile, ChatMessageItem, ChatReplyRef, ChatUser } from "@altek/ui/models/chat/chat-types";
import { photoUrlFor } from "@/features/directory/api/directory.api";
import type {
  ChatAttachmentRow,
  ChatConversationDetail,
  ChatConversationRow,
  ChatMessageRow,
  ChatSearchHit,
} from "../api/chat.types";

/**
 * The seam between the wire and the kit.
 *
 * WHY IT IS A SEPARATE, PURE MODULE. `ChatPanel` and `ChatList` speak `ChatMessageItem` and
 * `ChatListEntry`; the server speaks `ChatMessageRow` and `ChatConversationRow`. Doing the
 * translation inside the components that render would put four half-copies of it in the page,
 * the dock, the search results and the tests — and the copies drift, which is how a tombstoned
 * message ends up rendering as an empty bubble in one surface and as "Bu mesaj silindi." in
 * another. Everything here is a pure function of its arguments, so all of it is testable without
 * a DOM, a query client or a network.
 */

/** What a redacted row says. The row stays; only its body is gone (§14b: it is a tombstone). */
export const REDACTED_BODY = "Bu mesaj silindi.";
/**
 * «DOSYA KALDIRILDI» İZİ (KARARLAR K1).
 *
 * Sohbet eki tahliyesi açıkken (varsayılan KAPALI), yaş eşiğini geçen ekler mesajdan
 * kaldırılıyor. İz bırakmayan bir tahliye, konuşmayı geçmişe dönük sessizce yeniden yazmak
 * olurdu: okuyan kişi «ek gönderdim» diyen mesajın altında hiçbir şey görmez ve kendi
 * hatırladığından şüphe eder.
 *
 * İz şu an GÖVDEYE ekleniyor, çünkü kitin `ChatMessageItem` sözleşmesinde bunun için bir yer
 * yok. Doğru çözüm kitte ayrı bir alan (soluk, kendi satırında); o gelene kadar bu satır
 * söylenmesi gerekeni söylüyor. (Kit isteği: mesaj altına sistem notu yuvası.)
 */
export const REMOVED_ATTACHMENT_NOTE = "(Saklama süresi dolduğu için dosya kaldırıldı)";

/**
 * A person's photograph, as an `<img>` source.
 *
 * §9: pre-signed URLs force `attachment` and do not work in an `<img>`, so a photo flows through
 * the cookie-authenticated directory route instead. `hasPhoto: false` returns `undefined` rather
 * than a URL that would 404 — the avatar then draws initials, which is the intended fallback and
 * not a broken image.
 *
 * THE URL IS BUILT BY THE DIRECTORY (`photoUrlFor`), not a second time here. The copy that lived
 * in this file had no version parameter, so a replaced photograph stayed old in chat for the ten
 * minutes the route is cached while the directory showed the new one. `version` is the person's
 * `photoFileId` where the DTO carries it — directory rows do; the chat member DTO does not yet
 * (backend request), and there the URL stays unversioned.
 */
export function personPhotoUrl(
  userId: string,
  hasPhoto: boolean,
  version?: string | null,
): string | undefined {
  if (!hasPhoto) return undefined;
  return photoUrlFor(userId, version);
}

type MemberLike = { userId: string; displayName: string; hasPhoto: boolean };

export function toChatUser(member: MemberLike): ChatUser {
  const src = personPhotoUrl(member.userId, member.hasPhoto);
  return { id: member.userId, name: member.displayName, ...(src ? { src } : {}) };
}

/**
 * Everybody the surface can name, by user id.
 *
 * Built from the DETAIL roster when there is one and from the row's five-member preview
 * otherwise, because the rail's summary deliberately truncates. A sender who is not in the map
 * gets a neutral label rather than a blank: an unnamed bubble looks like a rendering fault, and
 * "Ayrılmış kullanıcı" is the honest reading of a member who has left the group.
 */
export function memberIndex(
  conversation: ChatConversationRow | ChatConversationDetail | undefined,
): Map<string, ChatUser> {
  const index = new Map<string, ChatUser>();
  if (!conversation) return index;

  const roster =
    "allMembers" in conversation && conversation.allMembers
      ? conversation.allMembers
      : conversation.members;

  for (const member of roster) index.set(member.userId, toChatUser(member));
  return index;
}

export const UNKNOWN_AUTHOR = "Ayrılmış kullanıcı";

/**
 * The author of a SYSTEM line (H9, 20 Ekim 2026). The kit draws every message as a bubble with an
 * author; a group event has none, so it is «Sistem». Its id is not a user id — `newestFromOthers`
 * and `firstUnreadMessageId` skip it, because the server never counts a system line as unread.
 */
export const SYSTEM_AUTHOR_ID = "system";

function authorOf(index: Map<string, ChatUser>, userId: string | null): ChatUser {
  if (userId === null) return { id: SYSTEM_AUTHOR_ID, name: "Sistem" };
  return index.get(userId) ?? { id: userId, name: UNKNOWN_AUTHOR };
}

/** The other party of a direct conversation — the one whose avatar and presence the rail shows. */
export function peerOf(
  conversation: ChatConversationRow | ChatConversationDetail | undefined,
  meId: string,
): ChatUser | undefined {
  if (!conversation || conversation.kind !== "DIRECT") return undefined;
  const roster =
    "allMembers" in conversation && conversation.allMembers
      ? conversation.allMembers
      : conversation.members;
  const other = roster.find((member) => member.userId !== meId) ?? roster[0];
  return other ? toChatUser(other) : undefined;
}

/**
 * KENDİYLE KONUŞMANIN ADI, KİŞİNİN KENDİ ADIDIR.
 *
 * Sunucu bu satıra kullanıcının verdiği başlığı taşıyor — pratikte «Kendime notlar» gibi bir şey
 * — ve o başlık listede bir KİŞİ gibi değil, bir klasör gibi okunuyordu: yanındaki her satır bir
 * insanın adıyken bu bir konu adıydı. WhatsApp'ın «(Siz)», Slack'in «you» rozetiyle çözdüğü şey
 * aynısı: satır yine bir kişi, ve o kişi okuyanın kendisi.
 *
 * İKİ KOŞUL BİRDEN aranıyor, çünkü tek başına ikisi de yanılır. `memberCount <= 1` tek başına,
 * karşı tarafı ayrılmış normal bir konuşmayı da yakalardı. «Listelenen herkes benim» tek başına
 * ise listenin ÖNİZLEME olduğunu unutur: satırdaki `members` kırpılmış bir örnek, ve iki kişilik
 * bir konuşmanın önizlemesinde bir tek ben görünürsem konuşma kendimle değildir.
 */
export function selfConversationName(
  conversation: ChatConversationRow | ChatConversationDetail | undefined,
  meId: string,
): string | undefined {
  if (!conversation || conversation.kind !== "DIRECT" || conversation.memberCount > 1) {
    return undefined;
  }
  const roster =
    "allMembers" in conversation && conversation.allMembers
      ? conversation.allMembers
      : conversation.members;
  if (roster.length === 0 || roster.some((member) => member.userId !== meId)) return undefined;
  const me = roster.find((member) => member.userId === meId);
  return me ? `${me.displayName} (Siz)` : undefined;
}

/**
 * Bir konuşmanın ekranda okunan adı — rayda, panel başlığında ve yuvanın şeridinde AYNI olanı.
 *
 * Tek bir yerden geçiyor, çünkü üç yüzeyin üçü de kendi başına `conversation.title` okusaydı
 * «(Siz)» eki bir yerde olur ötekinde olmazdı; aynı konuşmanın iki adı olması da tam olarak bu
 * şekilde başlar.
 */
export function conversationTitle(
  conversation: ChatConversationRow | ChatConversationDetail | undefined,
  meId: string,
): string | undefined {
  if (!conversation) return undefined;
  return selfConversationName(conversation, meId) ?? conversation.title;
}

/**
 * BİR KİŞİYLE ZATEN VAR OLAN BİREBİR KONUŞMA — yeni sohbet akışının ilk sorusu.
 *
 * §14b `POST /chat/conversations`'ın var olan konuşmayı döndürdüğünü söylüyor, ama ölçülen
 * davranış bu değildi: yuvadan bir kişi seçmek İKİNCİ bir konuşma satırı üretti ve listede aynı
 * kişi iki kez göründü (kullanıcının ekran görüntüsü: «altek_admin (Siz)» boş, hemen altında
 * mesajları olan ikinci bir tane). Sunucu ne yaparsa yapsın, elimizde zaten yüklü olan listede
 * cevap varken yazma isteği göndermek gereksiz — ve gereksiz yazma isteği, çiftlemenin kendisi.
 *
 * `memberCount` ile birlikte aranıyor: birebir konuşmanın satırındaki `members` bir önizleme, ve
 * bir grupta o kişinin görünmesi onunla BİREBİR konuşmam olduğu anlamına gelmiyor.
 */
export function findDirectConversationWith(
  rows: ReadonlyArray<ChatConversationRow>,
  meId: string,
  userId: string,
): string | null {
  const ceiling = userId === meId ? 1 : 2;
  const match = rows.find(
    (row) =>
      row.kind === "DIRECT" &&
      row.memberCount <= ceiling &&
      row.members.some((member) => member.userId === userId),
  );
  return match?.id ?? null;
}

export function attachmentFile(attachment: ChatAttachmentRow): ChatFile {
  /**
   * The id is the LINK id, not the file id.
   *
   * The download route is `/chat/messages/{messageId}/attachments/{linkId}/download`, so the
   * link id is the only one the surface can act on. Carrying the file id here would give the
   * download handler an identifier the endpoint does not accept, and the failure would be a 404
   * on a file that plainly exists.
   */
  return {
    id: attachment.linkId,
    name: attachment.name,
    mimeType: attachment.mimeType,
    sizeBytes: attachment.sizeBytes,
    /**
     * TARAMA DURUMU (K3). Gönderim anında her ek CLEAN — sunucu taranmamış eki zaten reddediyor.
     * Bu alan GERİ DOLDURMA için: hattan önce gönderilmiş bir ek sonradan zararlı çıkabiliyor ve
     * o zaman mesaj kalıyor, ekin yerinde «Zararlı içerik bulundu» yazıyor, indirme kapanıyor
     * (kit 3.17). Eki sessizce kaldırmak, konuşmada bir zamanlar bir dosya olduğunu da silerdi.
     */
    ...(isScanState(attachment.scan) ? { scan: attachment.scan } : {}),
  };
}

const SCAN_STATES = ["PENDING", "SCANNING", "CLEAN", "INFECTED", "SCAN_FAILED"] as const;

function isScanState(value: string | undefined): value is (typeof SCAN_STATES)[number] {
  return typeof value === "string" && (SCAN_STATES as readonly string[]).includes(value);
}

/**
 * One message, ready to render.
 *
 * `status` is set ONLY on the caller's own messages, because the kit's contract says so and
 * because it is true: the server tells the sender how far their message got and tells nobody
 * else. It is `sent` and never `delivered` or `read` — the wire carries no per-recipient
 * receipt for a message this client has already stored, and claiming "Okundu" from a row that
 * does not say so would be a comforting lie on the one surface where lying costs trust.
 */
export function messageItem(
  row: ChatMessageRow,
  options: { meId: string; members: Map<string, ChatUser> },
): ChatMessageItem {
  const author = authorOf(options.members, row.senderUserId);
  const isOwn = row.senderUserId !== null && row.senderUserId === options.meId;
  const files = row.attachments.map(attachmentFile);
  // Silinmiş mesajın eki zaten yok; iz yalnız TAHLİYE edilmiş eki anlatıyor.
  const removedNote =
    !row.redactedAt && row.removedAttachmentCount > 0 ? REMOVED_ATTACHMENT_NOTE : "";

  return {
    id: row.id,
    author,
    sentAt: row.createdAt,
    body: row.redactedAt
      ? REDACTED_BODY
      : [row.body ?? "", removedNote].filter((part) => part !== "").join("\n"),
    /**
     * THE KIT IS TOLD THE MESSAGE WAS WITHDRAWN (H5, 20 Ekim 2026). Until now the tombstone reached
     * it only as a sentence in `body`, so it drew «Bu mesaj silindi.» as an ordinary bubble — and,
     * on the reader's own message, would have offered «düzenle / sil / yanıtla» on it. With
     * `redactedAt` it draws its own withdrawn line and no controls (the server refuses them anyway).
     */
    ...(row.redactedAt ? { redactedAt: row.redactedAt } : {}),
    ...(files.length > 0 && !row.redactedAt ? { files } : {}),
    ...(isOwn ? { status: "sent" as const } : {}),
    ...(row.editedAt && !row.redactedAt ? { editedAt: row.editedAt } : {}),
    // A withdrawn reply loses its quote too (seen in the 20 Ekim two-account round): the kit drew
    // the quoted message above «Bu mesaj silindi», as if the withdrawn row still said something.
    ...(row.replyTo && !row.redactedAt
      ? {
          replyTo: {
            id: row.replyTo.id,
            authorName: authorOf(options.members, row.replyTo.senderUserId).name,
            excerpt: row.replyTo.preview,
          },
        }
      : {}),
  };
}

/**
 * Cursor pages → the order `ChatPanel` renders in.
 *
 * The endpoint answers newest first and pages BACKWARDS, so page 2 is older than page 1. The
 * panel renders oldest first (its own comment explains why `column-reverse` was rejected). The
 * whole reconciliation is this one function: flatten in page order, then reverse. Doing it at a
 * call site was tried and produced a thread that loaded upside down in the dock and right way up
 * on the page.
 */
export function orderOldestFirst(pages: ReadonlyArray<{ rows: ChatMessageRow[] }>): ChatMessageRow[] {
  const flattened: ChatMessageRow[] = [];
  for (const page of pages) flattened.push(...page.rows);
  return flattened.reverse();
}

/**
 * Where the "Yeni mesajlar" rule goes.
 *
 * THE SERVER DOES NOT SEND A `firstUnreadId`; it sends a COUNT. So the divider is derived: walk
 * backwards over the thread counting messages that are not the reader's own, and stop on the
 * `unreadCount`-th. That is exactly the set the badge is counting, which is what keeps the rule
 * and the number agreeing with each other.
 *
 * Returns `undefined` when there is nothing unread, and when the unread run is longer than the
 * history currently loaded — a rule drawn at the top of a partially loaded thread would claim
 * the oldest message on screen is the first new one, which is usually false and always
 * unverifiable from here.
 */
export function firstUnreadMessageId(
  ordered: ReadonlyArray<ChatMessageItem>,
  unreadCount: number,
  meId: string,
): string | undefined {
  if (unreadCount <= 0) return undefined;

  let seen = 0;
  for (let index = ordered.length - 1; index >= 0; index -= 1) {
    const message = ordered[index];
    // A SYSTEM line is never unread on the server (H9), so it is not part of the count either.
    if (!message || message.author.id === meId || message.author.id === SYSTEM_AUTHOR_ID) continue;
    seen += 1;
    if (seen === unreadCount) return message.id;
  }
  return undefined;
}

/**
 * The newest message somebody else wrote — the id the read cursor is moved to.
 *
 * Own messages are skipped because marking your own message read moves the cursor without
 * clearing anything, and the server's cursor only travels forward: a wrong id sent once cannot
 * be taken back.
 */
export function newestFromOthers(
  ordered: ReadonlyArray<ChatMessageItem>,
  meId: string,
): string | undefined {
  for (let index = ordered.length - 1; index >= 0; index -= 1) {
    const message = ordered[index];
    if (message && message.author.id !== meId && message.author.id !== SYSTEM_AUTHOR_ID) return message.id;
  }
  return undefined;
}

/**
 * A conversation row → a rail entry.
 *
 * `kind` IS MAPPED SO THE RAIL AND THE PANEL AGREE. The server has DIRECT and GROUP; the kit has
 * direct, group and channel — but `ChatList` only SECTIONS by pinned/channel/direct and buckets
 * anything that is not `channel` into `direct`, so a nine-person group listed as `group` appeared
 * under the heading "Doğrudan mesajlar". `ChatThread` already renders the same conversation as
 * the kit's `channel` (a hash for a named room, rather than one arbitrary member's photograph),
 * so `channel` is the mapping that makes the two surfaces say the same thing about one row.
 *
 * The right long-term fix is a `group` section in the kit's own `SECTION_TITLES`; that is a
 * shared change and is requested in this task's report rather than made here.
 */
export function conversationEntry(
  row: ChatConversationRow,
  options: { meId: string; pinned: boolean },
): ChatListEntry {
  const peer = peerOf(row, options.meId);
  const last = row.lastMessage;

  return {
    id: row.id,
    kind: row.kind === "GROUP" ? "channel" : "direct",
    name: conversationTitle(row, options.meId) ?? row.title,
    ...(peer?.src ? { avatarSrc: peer.src } : {}),
    ...(last
      ? {
          lastMessage: {
            authorName: authorOf(memberIndex(row), last.senderUserId).name,
            excerpt: last.preview,
            sentAt: last.createdAt,
            mine: last.senderUserId === options.meId,
          },
        }
      : {}),
    unreadCount: row.unreadCount,
    muted: row.muted,
    pinned: options.pinned,
    /**
     * Satır menüsü arşivliyorsa, arşivli olup olmadığını da bilmeli — yoksa arşivli bir
     * konuşmaya «Arşivle» der ve basan kişi hiçbir şeyin değişmediğini görür.
     */
    archived: row.archived,
    /**
     * İZİN SATIR BAŞINA, liste başına değil — ve bu ayrımı kaçırmak tıkla-ve-patla üretiyordu.
     *
     * Sunucunun kuralı: DM'de iki taraf da arşivler, GRUPTA yalnız sahip (sıradan üyeye 403
     * `OWNER_REQUIRED`). Kit `v3.4.0`'a kadar menü girdisini işleyici verilince HER satıra
     * çiziyordu; o hâliyle bağlamak, üyesi olup sahibi olmadığın her grupta basınca hata veren
     * bir «Arşivle» demekti.
     *
     * `archived` ile AYNI ANDA eşleniyor, çünkü ikisi aynı menü girdisinin iki yarısı: biri
     * hangi yönde çizileceğini, öbürü çizilip çizilmeyeceğini söylüyor. Ayrı yerlerde
     * eşlenselerdi, birini ekleyip öbürünü unutmak kolay olurdu.
     */
    canArchive: canArchiveConversation(row),
    ...(row.kind === "GROUP" ? { members: row.memberCount } : {}),
  };
}

/**
 * The rail's order: most recent activity first.
 *
 * `ChatList` renders rows in the order it is given and says so — it refuses to sort, because
 * only the caller knows what "last activity" means. Here it means the last message, falling back
 * to the conversation's own `updatedAt` for a group that has been created but never written in;
 * without the fallback a brand-new conversation sorts to the bottom of the rail, under threads
 * from last month, and the person who just created it cannot find it.
 */
export function byRecentActivity(rows: ReadonlyArray<ChatConversationRow>): ChatConversationRow[] {
  return [...rows].sort((left, right) => {
    const leftAt = left.lastMessage?.createdAt ?? left.updatedAt;
    const rightAt = right.lastMessage?.createdAt ?? right.updatedAt;
    return rightAt.localeCompare(leftAt);
  });
}

/**
 * May THIS reader archive this conversation?
 *
 * §14b: "DM'de iki taraf da arşivler; grupta sahip" — `POST …/archive` answers an ordinary
 * member of a GROUP with 403 `PERMISSION_DENIED` / `OWNER_REQUIRED`. The client only ever HIDES,
 * so the rule is asked here rather than discovered from an error toast after the click.
 *
 * A pure predicate rather than a condition inside the page's JSX: this is the one line that
 * decides whether an action a person can see exists, and a rule buried in a menu array can only
 * be tested by driving an overlay.
 */
export function canArchiveConversation(
  conversation: Pick<ChatConversationDetail, "kind" | "role"> | undefined,
): boolean {
  if (!conversation) return false;
  return conversation.kind === "DIRECT" || conversation.role === "OWNER";
}

/**
 * What the composer says when it will not let you type, or `null` when it will.
 *
 * Three separate reasons, kept apart because the fixes are different: an archived thread is
 * unarchived, a missing grant is given by an administrator, and a support session is ended by
 * the administrator who started it. Collapsing them into "Mesaj gönderilemiyor" is what produces
 * the support call that has to establish which of the three it was.
 */
export function composerBlockReason(input: {
  canSend: boolean;
  archived: boolean;
  /** A RESTRICTED support session (`isRestrictedSupportSession`); in development mode it is false. */
  impersonating: boolean;
}): string | null {
  if (input.impersonating) return "Destek oturumunda mesaj gönderilemez.";
  if (!input.canSend) return "Mesaj göndermek için yetkiniz yok.";
  if (input.archived) return "Bu sohbet arşivlendi. Yazmak için arşivden çıkarın.";
  return null;
}

/** How much of a message the composer's quote strip shows — the kit clamps it to two lines anyway. */
const REPLY_EXCERPT_CHARS = 140;

/**
 * The quote a reply carries in the composer (H5, 20 Ekim 2026) — built from the message on screen,
 * so the strip names the author the bubble names. The server stores only the id
 * (`replyToMessageId`) and draws its own quote from the live message once the reply is sent.
 */
export function replyRefOf(message: ChatMessageItem): ChatReplyRef {
  const flat = message.body.replace(/\s+/g, " ").trim();
  const excerpt =
    flat.length > REPLY_EXCERPT_CHARS
      ? `${flat.slice(0, REPLY_EXCERPT_CHARS - 1).trimEnd()}…`
      : flat || (message.files && message.files.length > 0 ? `Dosya: ${message.files[0]?.name ?? ""}` : "");
  return { id: message.id, authorName: message.author.name, excerpt };
}

/**
 * A server search hit → the rail's «Mesajlar» row (H6). The conversation is named by the SERVER
 * (`conversationTitle`), because the hit may sit in a conversation the rail has not loaded; the
 * «(Siz)» spelling of a note-to-self is the rail's, so a loaded row's name wins when there is one.
 */
export function searchHitItem(hit: ChatSearchHit, loadedName?: string): ChatMessageHit {
  return {
    id: hit.messageId,
    conversationId: hit.conversationId,
    conversationName: loadedName ?? hit.conversationTitle,
    conversationKind: hit.conversationKind === "GROUP" ? "channel" : "direct",
    authorName: hit.sender?.displayName ?? "Sistem",
    excerpt: hit.snippet,
    sentAt: hit.createdAt,
  };
}
